import { Stack } from '@mantine/core';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import { GovEvaluationHeader } from '@/module/GovEvaluation/components/GovEvaluationHeader';
import { getGovEvaluationOptions } from '@/module/GovEvaluation/model/GovEvaluationQuery';
import { AuthorGovEvaluationHeader } from '@/module/GovEvaluation/perRole/AuthorGovEvaluationHeader';
import { DepartmentAdminGovEvaluationHeader } from '@/module/GovEvaluation/perRole/DepartmentAdminGovEvaluationHeader';
import { DisciplineAdminGovEvaluationHeader } from '@/module/GovEvaluation/perRole/DisciplineAdminGovEvaluationHeader';
import { FacultyAdminGovEvaluationHeader } from '@/module/GovEvaluation/perRole/FacultyAdminGovEvaluationHeader';
import { UniversityAdminGovEvaluationHeader } from '@/module/GovEvaluation/perRole/UniversityAdminGovEvaluationHeader';

import { NoContentPlaceholder } from '@/core/components';

import { useCurrentUser } from '@feature/security';

const evaluationSearchSchema = z.object({
  userId: z.coerce.number().nullish(),
  disciplineId: z.coerce.number().nullish(),
  departmentId: z.coerce.number().nullish(),
  facultyId: z.coerce.number().nullish(),
});

export const Route = createFileRoute('/_auth/gov-evaluation/_layout/$evaluationId/_layout')({
  validateSearch: evaluationSearchSchema,
  loader: async ({ params: { evaluationId }, context: { queryClient } }) => {
    const evaluation = await queryClient.ensureQueryData(getGovEvaluationOptions(evaluationId));

    return {
      evaluation,
      crumb: evaluation.title,
    };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { evaluationId } = Route.useParams();
  const { data: evaluation } = useSuspenseQuery(getGovEvaluationOptions(evaluationId));

  const { currentRole } = useCurrentUser();
  const hasAccess =
    ['AUTHOR', 'UNIVERSITY_ADMIN', 'DISCIPLINE_ADMIN', 'DEPARTMENT_ADMIN', 'FACULTY_ADMIN'].indexOf(currentRole) !== -1;

  if (!hasAccess) {
    return (
      <NoContentPlaceholder title="Nu aveți acces la acest ecran cu acest rol. Asigurați-vă că folosiți un rol de autor sau șef de disciplină / director departament sau decan" />
    );
  }

  return (
    <Stack>
      <GovEvaluationHeader evaluation={evaluation} />
      {currentRole === 'UNIVERSITY_ADMIN' && <UniversityAdminGovEvaluationHeader />}
      {currentRole === 'FACULTY_ADMIN' && <FacultyAdminGovEvaluationHeader />}
      {currentRole === 'DEPARTMENT_ADMIN' && <DepartmentAdminGovEvaluationHeader />}
      {currentRole === 'DISCIPLINE_ADMIN' && <DisciplineAdminGovEvaluationHeader />}
      {currentRole === 'AUTHOR' && <AuthorGovEvaluationHeader />}
    </Stack>
  );
}
